import type { Site, User } from './types';
import { getSitesByUser, getUserById, replaceUserSites, upsertLocalUser } from './store';

export async function getApiHealth(): Promise<{ ok?: boolean; storage?: string } | null> {
  try {
    const res = await fetch('/api/health');
    if (!res.ok) return null;
    return (await res.json()) as { ok?: boolean; storage?: string };
  } catch {
    return null;
  }
}

export async function isApiUp(): Promise<boolean> {
  const health = await getApiHealth();
  return Boolean(health?.ok);
}

export async function isMysqlApi(): Promise<boolean> {
  const health = await getApiHealth();
  return health?.storage === 'mysql';
}

export async function syncAccount(user: User, sites?: Site[], opts?: { replace?: boolean }) {
  const payloadSites = sites || getSitesByUser(user.id);
  const res = await fetch('/api/account/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user,
      sites: payloadSites,
      replace: Boolean(opts?.replace),
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { message?: string; sites?: Site[] };
  if (!res.ok) {
    throw new Error(data.message || '同步站点失败');
  }
  if (Array.isArray(data.sites)) {
    replaceUserSites(user.id, data.sites);
  }
}

export async function syncCurrentUser(userId: string, opts?: { replace?: boolean }) {
  const user = getUserById(userId);
  if (user) await syncAccount(user, getSitesByUser(userId), opts);
}

export async function pullAccount(token: string) {
  try {
    const res = await fetch(`/api/account/pull?token=${encodeURIComponent(token)}`);
    if (res.status === 401) return { ok: false, unauthorized: true };
    if (!res.ok) return null;
    return (await res.json()) as { ok: boolean; user?: User; sites?: Site[]; unauthorized?: boolean };
  } catch {
    return null;
  }
}

export async function loginRemote(email: string, password: string) {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
    const data = await res.json();
    if (!res.ok) return null;
    return data as { ok: boolean; user: User; sites: Site[] };
  } catch {
    return null;
  }
}

export async function clearRemoteVisits(token: string, code: string) {
  if (!token || !code) return;
  try {
    await fetch('/api/visits/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, code }),
    });
  } catch {
    /* 无后端时忽略 */
  }
}

export function applyRemoteAccount(user: User, sites: Site[]) {
  const saved = upsertLocalUser(user);
  replaceUserSites(saved.id, sites || []);
  return saved;
}
